/**
 * AddTeachingSubjectDialog — modelled on the Staff Profile "Add Teaching
 * Subject" modal so admins get the same UX in two places.
 *
 * The class form no longer offers "Create New Subject". Global subject
 * creation stays where it belongs (subject management). Here admins can
 * only compose a class from grade+subject pairs that ALREADY exist in
 * the system, honouring the teacher's teaching profile.
 *
 * Key behaviours (matches the spec):
 *   • Subject dropdown lists distinct existing subjects the current
 *     staff is qualified to teach (the source list is the same
 *     `subjectGRadeDataList` the form already renders).
 *   • Selecting a subject AUTO-SELECTS the grades already associated
 *     with that subject for this teacher. The admin can then remove
 *     or keep any grade.
 *   • Grade rows already added to the current class show as
 *     "Already assigned" and are disabled — no duplicate pairs.
 *   • On Save, returns the newly-selected `grade_subject_id` values
 *     to the parent, which appends them to the class form's state.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Typography, Box, Button, FormControl, InputLabel,
  Select, MenuItem, Checkbox, ListItemText, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IGrade } from "../../../Api/Interface/api.interface";
import {
  SubjectOption,
  buildSubjectOptionsFromGrades,
  resolvePreselectedGrades,
} from "./addTeachingSubjectHelpers";

// Re-export the pure helpers so any existing importer of this module
// continues to work while the tested logic lives in a React-free file.
export type { SubjectOption } from "./addTeachingSubjectHelpers";
export {
  buildSubjectOptionsFromGrades,
  resolvePreselectedGrades,
} from "./addTeachingSubjectHelpers";

export interface AddTeachingSubjectDialogProps {
  open: boolean;
  onClose: () => void;
  /** Same array ClassForm receives — grades, each with a
   *  `selected_sub_array` of {id: grade_subject_id, subject_id,
   *  is_assigned_to_staff}. Kept as the single source of truth. */
  subjectGRadeDataList: IGrade[];
  /** Lookup of subject_id → subject name (already available in ClassForm). */
  subjectMap: Map<number, string>;
  /** Currently-selected grade_subject_ids on the class form. Used to mark
   *  pairs "Already assigned" and prevent duplicates. */
  selectedGradeSubjectIds: number[];
  /** Grade_subject_ids the form marks as protected (in use by real
   *  sessions/enrolments). Never returned in `onAdd`. */
  protectedIds?: Set<number>;
  /** Called with the NEWLY-added grade_subject_ids (not the whole set).
   *  Parent appends them to its selectedSubject state. */
  onAdd: (newlyAddedGradeSubjectIds: number[]) => void;
  /** Optional class name shown under the title. */
  contextLabel?: string;
}

const AddTeachingSubjectDialog: React.FC<AddTeachingSubjectDialogProps> = ({
  open,
  onClose,
  subjectGRadeDataList,
  subjectMap,
  selectedGradeSubjectIds,
  protectedIds,
  onAdd,
  contextLabel,
}) => {
  // Derive available subjects (see buildSubjectOptionsFromGrades in the
  // helper block above). One list per (subject_id) grouping teachable
  // grade+subject pair-ids.
  const subjectOptions: SubjectOption[] = useMemo(
    () => buildSubjectOptionsFromGrades(subjectGRadeDataList, subjectMap),
    [subjectGRadeDataList, subjectMap],
  );

  const alreadyOnClass = useMemo(
    () => new Set<number>(selectedGradeSubjectIds || []),
    [selectedGradeSubjectIds],
  );

  const [subjectId, setSubjectId] = useState<number | "">("");
  const [gradeSubjectIds, setGradeSubjectIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const selectedSubject = useMemo(
    () => subjectOptions.find((s) => s.subject_id === subjectId) || null,
    [subjectOptions, subjectId],
  );

  // Reset when the dialog opens; the parent may cycle it multiple times
  // and each open should feel like a fresh picker.
  useEffect(() => {
    if (!open) return;
    setSubjectId("");
    setGradeSubjectIds([]);
    setSaving(false);
  }, [open]);

  // Auto-select the subject's associated grades on subject change (see
  // resolvePreselectedGrades in the helper block above).
  useEffect(() => {
    setGradeSubjectIds(resolvePreselectedGrades(selectedSubject, alreadyOnClass, protectedIds));
  }, [selectedSubject, alreadyOnClass, protectedIds]);

  const gradeRows = useMemo(() => {
    if (!selectedSubject) return [] as Array<{
      grade_subject_id: number;
      grade_name: string;
      alreadyOnClass: boolean;
    }>;
    return selectedSubject.pairs.map((p) => ({
      grade_subject_id: p.grade_subject_id,
      grade_name: p.grade_name,
      alreadyOnClass: alreadyOnClass.has(p.grade_subject_id),
    }));
  }, [selectedSubject, alreadyOnClass]);

  const selectableCount = gradeRows.filter((g) => !g.alreadyOnClass).length;
  const canSave = !!subjectId && gradeSubjectIds.length > 0 && !saving;

  const handleSelectAllAvailable = () =>
    setGradeSubjectIds(gradeRows.filter((g) => !g.alreadyOnClass).map((g) => g.grade_subject_id));
  const handleClearGrades = () => setGradeSubjectIds([]);

  const handleSave = () => {
    if (!canSave) return;
    setSaving(true);
    // Guard against double-add and against re-adding an "already on class"
    // pair even if a race put it back into the working set.
    const newlyAdded = gradeSubjectIds.filter((id) => !alreadyOnClass.has(id));
    onAdd(newlyAdded);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Add Teaching Subject</Typography>
          {contextLabel && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contextLabel}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel shrink>Subject</InputLabel>
            <Select
              label="Subject"
              notched
              displayEmpty
              value={subjectId}
              onChange={(e) => setSubjectId(Number(e.target.value) || "")}
              renderValue={(selected) => {
                const id = Number(selected);
                if (!id) {
                  return (
                    <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                      Select a subject
                    </Typography>
                  );
                }
                const name = subjectOptions.find((s) => s.subject_id === id)?.name;
                return <Typography sx={{ fontSize: 13 }}>{name || ""}</Typography>;
              }}
            >
              {subjectOptions.map((s) => (
                <MenuItem key={s.subject_id} value={s.subject_id}>{s.name}</MenuItem>
              ))}
              {subjectOptions.length === 0 && (
                <MenuItem disabled>
                  <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                    No subjects available. Assign teaching subjects to the staff first.
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={!subjectId}>
            <InputLabel shrink>Grades</InputLabel>
            <Select
              multiple
              notched
              displayEmpty
              label="Grades"
              value={gradeSubjectIds}
              onChange={(e) => {
                const v = e.target.value;
                setGradeSubjectIds(typeof v === "string" ? [] : (v as number[]));
              }}
              renderValue={(selected) => {
                if (!subjectId) {
                  return (
                    <Typography sx={{ fontSize: 13, color: "text.disabled" }}>
                      Select a subject first
                    </Typography>
                  );
                }
                const ids = selected as number[];
                if (ids.length === 0) return <Typography sx={{ fontSize: 13, color: "text.disabled" }}>Select one or more grades</Typography>;
                const names = ids
                  .map((id) => gradeRows.find((g) => g.grade_subject_id === id)?.grade_name)
                  .filter(Boolean) as string[];
                return names.join(", ");
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
            >
              {gradeRows.length === 0 && subjectId && (
                <MenuItem disabled>
                  <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                    No grades are configured for this subject.
                  </Typography>
                </MenuItem>
              )}
              {gradeRows.map((g) => (
                <MenuItem key={g.grade_subject_id} value={g.grade_subject_id} disabled={g.alreadyOnClass}>
                  <Checkbox size="small"
                    checked={gradeSubjectIds.includes(g.grade_subject_id) || g.alreadyOnClass}
                    disabled={g.alreadyOnClass}
                    sx={{ py: 0.25, mr: 0.5 }} />
                  <ListItemText
                    primary={g.grade_name}
                    secondary={g.alreadyOnClass ? "Already assigned" : undefined}
                    primaryTypographyProps={{ fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 11, color: "text.disabled" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {subjectId && gradeRows.length > 0 && selectableCount === 0 && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              All available grades for this subject are already assigned to the class.
            </Typography>
          )}

          {subjectId && selectableCount > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: -0.5 }}>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {gradeSubjectIds.length} of {selectableCount} available selected
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" onClick={handleSelectAllAvailable} sx={{ textTransform: "none", fontSize: 11.5 }}>Select All Available</Button>
                <Button size="small" onClick={handleClearGrades} sx={{ textTransform: "none", fontSize: 11.5 }} disabled={gradeSubjectIds.length === 0}>Clear</Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}>
          {saving ? <CircularProgress size={16} sx={{ mr: 1, color: "inherit" }} /> : null}
          Add Subject Assignment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeachingSubjectDialog;
